import { useRef, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";

import { animate, canAnimate, duration, easing } from "@/shared/lib";
import { tactile } from "@/shared/telegram";

import { Overlay } from "./Overlay";
import { useOverlay } from "./overlay-context";

/**
 * Нижний шит. Figma: `Overlay · Robot Filters` (28:46) — 390 в ширину,
 * скруглён на 28, паддинг 20, шаг 14, сверху хват 44×4.
 *
 * Движение (FE-13): шит выезжает снизу и уходит туда же, а хват работает
 * по-настоящему — за него шит тянется пальцем и закрывается свайпом вниз.
 * Для приложения, живущего внутри Telegram, это главный нативный жест: без
 * него хват в макете остаётся декорацией.
 *
 * Отклик (FE-14): жест щёлкает в момент, когда протяжка пересекла порог
 * закрытия, — то есть пока палец ещё на стекле. Отклик после отпускания
 * сообщал бы об уже случившемся; отклик на пороге сообщает, что отпустить
 * можно прямо сейчас, и превращает свайп из угадывания в управление.
 */
interface SheetProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}

export function Sheet({ title, onClose, children, footer }: SheetProps) {
  return (
    <Overlay onClose={onClose} labelledBy="sheet-title">
      <SheetBody title={title} footer={footer}>
        {children}
      </SheetBody>
    </Overlay>
  );
}

/** Доля высоты, после которой отпущенный шит закрывается, а не возвращается. */
const DISMISS_RATIO = 3;
/** Скорость броска в пикселях за миллисекунду: короткий резкий свайп. */
const DISMISS_VELOCITY = 0.5;

interface DragState {
  startY: number;
  startTime: number;
  offset: number;
  pointerId: number;
  /** Порог закрытия уже пройден — щелчок на нём отдаётся ровно один раз. */
  armed: boolean;
}

function SheetBody({ title, children, footer }: Omit<SheetProps, "onClose">) {
  const { requestClose } = useOverlay();
  const ref = useRef<HTMLDivElement>(null);
  const drag = useRef<DragState | undefined>(undefined);

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    const element = ref.current;
    if (!element || !canAnimate()) return;

    // Если шит ещё выезжает, доигрываем вход мгновенно: тянуть элемент,
    // который параллельно анимируется, — гарантированный рывок.
    for (const animation of element.getAnimations()) animation.finish();

    drag.current = {
      startY: event.clientY,
      startTime: event.timeStamp,
      offset: 0,
      pointerId: event.pointerId,
      armed: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const state = drag.current;
    const element = ref.current;
    if (!state || !element) return;

    // Вверх шит не тянется: он уже прижат к нижнему краю.
    state.offset = Math.max(0, event.clientY - state.startY);
    element.style.transform = `translate3d(0, ${String(state.offset)}px, 0)`;

    // Порог тот же, что и при отпускании ниже, — иначе щелчок обещал бы
    // закрытие, которого не произойдёт.
    const passed = state.offset > element.offsetHeight / DISMISS_RATIO;
    if (passed !== state.armed) {
      state.armed = passed;
      if (passed) tactile("surface");
    }
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLElement>) => {
    const state = drag.current;
    const element = ref.current;
    drag.current = undefined;
    if (!state || !element) return;

    // На `pointercancel` захват уже снят системой, и повторный вызов бросает.
    if (event.currentTarget.hasPointerCapture(state.pointerId)) {
      event.currentTarget.releasePointerCapture(state.pointerId);
    }

    const elapsed = Math.max(1, event.timeStamp - state.startTime);
    const velocity = state.offset / elapsed;
    const dismissed =
      state.offset > element.offsetHeight / DISMISS_RATIO || velocity > DISMISS_VELOCITY;

    if (dismissed) {
      // Довести шит до низа самому: `requestClose` гасит затемнение и снимает
      // шит с дерева через ту же длительность, так что стыка не видно.
      animate(
        element,
        [
          { transform: `translate3d(0, ${String(state.offset)}px, 0)` },
          { transform: "translate3d(0, 100%, 0)" },
        ],
        { duration: duration("base"), easing: easing("in-quint"), fill: "forwards" },
      );
      requestClose();
      return;
    }

    element.style.transform = "";
    animate(
      element,
      [
        { transform: `translate3d(0, ${String(state.offset)}px, 0)` },
        { transform: "translate3d(0, 0, 0)" },
      ],
      { duration: duration("slow"), easing: easing("spring") },
    );
  };

  return (
    <div
      ref={ref}
      className={[
        "mt-auto flex w-full flex-col items-center gap-[14px] rounded-[28px] bg-background-elevated p-5",
        // Запас снизу под жестовую полосу: без него кнопка шита упирается в
        // самый край стекла, где её перехватывает системный жест.
        "pb-[max(calc(var(--spacing)*5),env(safe-area-inset-bottom))]",
        // Высокий шит (сумма + пресеты + кнопка) на коротком экране уезжал бы
        // вверх за границу окна вместе с кнопкой: `mt-auto` прижимает его к
        // низу, а расти он может только вверх. Ограничение по высоте оставляет
        // кнопку на месте и отдаёт лишнее прокрутке содержимого.
        "max-h-[calc(100dvh-env(safe-area-inset-top))] overflow-y-auto",
        "animate-sheet-in [[data-state=closing]_&]:animate-sheet-out",
      ].join(" ")}
    >
      {/* Хват и заголовок — зона перетаскивания. Всё, что ниже, остаётся
          обычным содержимым: иначе свайп по списку фильтров закрывал бы шит
          вместо прокрутки. `touch-none` отдаёт вертикальный жест нам, а не
          прокрутке страницы. */}
      <header
        className="flex w-full touch-none flex-col items-center gap-[14px]"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <span className="h-1 w-11 rounded-full bg-handle" aria-hidden="true" />
        <h2 id="sheet-title" className="text-h3 font-semibold text-text-primary">
          {title}
        </h2>
      </header>

      {children}
      {footer}
    </div>
  );
}
